import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Layers } from "lucide-react";
import api from "@/lib/api";
import StatusBadge from "@/components/StatusBadge";
import ListSearchBar from "@/components/ListSearchBar";
import { useDebounced } from "@/hooks/usePagedSearch";
import { useAuth } from "@/contexts/AuthContext";
import { CARD_IMPORT_ROLES } from "@/constants/roles";
import { fmtDate } from "@/lib/utils";

const fmt = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}`;

const KINDS = [
  { value: "", label: "All" },
  { value: "ramp", label: "Ramp" },
  { value: "expense", label: "Expense Log" },
  { value: "po", label: "Purchase Orders" },
  { value: "invoice", label: "AP Invoices" },
];
const KIND_STYLE = { ramp: "ss-pill-purple", expense: "ss-pill-amber", po: "ss-pill-sky", invoice: "ss-pill-green" };
const METHODS = [
  { value: "", label: "Any payment" },
  { value: "ramp", label: "Ramp" },
  { value: "company_card", label: "Company card" },
  { value: "personal_card", label: "Personal card" },
  { value: "card", label: "Card (PO)" },
  { value: "cash", label: "Cash" },
  { value: "check", label: "Check" },
  { value: "ap", label: "AP / on account" },
];
const METHOD_LABEL = Object.fromEntries(METHODS.map((m) => [m.value, m.label]));
const PAGE = 50;

// 2026-10-27 — Ramp charges, Expense Log entries (cash, check, card, AP), POs
// and AP invoices in one list. Read-only: a row opens the record's own page.
// Purchasers see only their own; admins and accounting see everyone's.
export default function Transactions() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const seesAll = CARD_IMPORT_ROLES.includes(user?.role);
  const [kind, setKind] = useState("");
  const [method, setMethod] = useState("");
  const [jobId, setJobId] = useState("");
  const [mine, setMine] = useState(false);
  const [search, setSearch] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [jobs, setJobs] = useState([]);
  const [items, setItems] = useState(null);
  const [counts, setCounts] = useState(null);
  const [nextOffset, setNextOffset] = useState(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const q = useDebounced(search);

  useEffect(() => { api.get("/contractor/jobs").then((r) => setJobs(r.data)).catch(() => {}); }, []);

  const params = (offset) => Object.fromEntries(Object.entries({
    q, kind, method, job_id: jobId, date_from: dateFrom, date_to: dateTo, scope: mine ? "mine" : "", limit: PAGE, offset,
  }).filter(([, v]) => v !== "" && v != null));

  useEffect(() => {
    let live = true;
    api.get("/contractor/transactions", { params: params(0) }).then((r) => {
      if (!live) return;
      setItems(r.data.items); setNextOffset(r.data.next_offset);
      if (r.data.counts) setCounts(r.data.counts);
    }).catch(() => live && setItems([]));
    return () => { live = false; };
  }, [q, kind, method, jobId, dateFrom, dateTo, mine]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadMore = async () => {
    setLoadingMore(true);
    try {
      const { data } = await api.get("/contractor/transactions", { params: params(nextOffset) });
      setItems((prev) => [...prev, ...data.items]); setNextOffset(data.next_offset);
    } finally {
      setLoadingMore(false);
    }
  };

  return (
    <div className="p-6 md:p-8" data-testid="transactions-page">
      <h1 className="text-2xl font-extrabold tracking-tight flex items-center gap-2.5"><Layers size={22} className="text-sky-500" /> All Transactions</h1>
      <p className="text-slate-500 text-sm mt-1 max-w-2xl">
        Ramp charges, Expense Log entries (cash, check, card, AP), purchase orders and AP invoices in one place.{" "}
        {seesAll ? "You see everyone's." : "You see only your own."}
      </p>

      <ListSearchBar value={search} onChange={setSearch} placeholder="Search number, vendor or merchant, purchaser, cardholder…" testId="transactions-search">
        <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} title="From" className="h-10 px-3 rounded-lg border border-slate-200 text-[13px]" />
        <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} title="To" className="h-10 px-3 rounded-lg border border-slate-200 text-[13px]" />
        <select value={method} onChange={(e) => setMethod(e.target.value)} data-testid="transactions-method" className="h-10 px-3 rounded-lg border border-slate-200 text-[13px]">
          {METHODS.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
        </select>
        <select value={jobId} onChange={(e) => setJobId(e.target.value)} data-testid="transactions-job" className="h-10 px-3 rounded-lg border border-slate-200 text-[13px] max-w-[220px]">
          <option value="">All jobs</option>
          {jobs.map((j) => <option key={j.id} value={j.id}>{j.code} — {j.name}</option>)}
        </select>
        {seesAll && (
          <label className="flex items-center gap-1.5 text-[12.5px] text-slate-600 px-1">
            <input type="checkbox" checked={mine} onChange={(e) => setMine(e.target.checked)} data-testid="transactions-mine" /> Mine only
          </label>
        )}
      </ListSearchBar>

      <div className="flex gap-2 mt-4 border-b border-slate-100 overflow-x-auto">
        {KINDS.map((k) => (
          <button key={k.value || "all"} onClick={() => setKind(k.value)} data-testid={`transactions-kind-${k.value || "all"}`}
            className={`px-3.5 py-2 text-[12.5px] font-semibold border-b-2 -mb-px whitespace-nowrap ${kind === k.value ? "border-sky-500 text-sky-600" : "border-transparent text-slate-500 hover:text-slate-700"}`}>
            {k.label}{counts && counts[k.value || "all"] != null ? ` (${counts[k.value || "all"].toLocaleString()})` : ""}
          </button>
        ))}
      </div>

      {items === null ? <div className="py-10 text-center text-slate-400 text-sm">Loading…</div> : (
        <div className="overflow-x-auto mt-4">
          <table className="w-full text-[13px]" data-testid="transactions-table">
            <thead>
              <tr className="text-[11px] uppercase tracking-wide text-slate-400 font-semibold border-b border-slate-100">
                <th className="text-left py-2">Date</th><th className="text-left py-2">Type</th><th className="text-left py-2">Number</th>
                <th className="text-left py-2">Vendor / Merchant</th><th className="text-left py-2">Job</th><th className="text-left py-2">Paid with</th>
                <th className="text-left py-2">Who</th><th className="text-right py-2">Amount</th><th className="text-left py-2 pl-3">Status</th>
              </tr>
            </thead>
            <tbody>
              {items.map((t) => (
                <tr key={`${t.kind}-${t.id}`} onClick={() => navigate(t.url)} className="cursor-pointer hover:bg-slate-50" data-testid={`transaction-row-${t.kind}-${t.id}`}>
                  <td className="py-2.5 border-t border-slate-100 text-slate-500 whitespace-nowrap">{fmtDate(t.date)}</td>
                  <td className="py-2.5 border-t border-slate-100"><span className={`ss-pill ${KIND_STYLE[t.kind] || "ss-pill-gray"}`}>{t.kind_label}</span></td>
                  <td className="py-2.5 border-t border-slate-100 mono text-[12px]">{t.number || "—"}</td>
                  <td className="py-2.5 border-t border-slate-100 font-medium">{t.party || "—"}</td>
                  <td className="py-2.5 border-t border-slate-100 text-[12.5px]">
                    {t.job ? <span title={t.job.name}>{t.job.code}{t.job_count > 1 ? ` +${t.job_count - 1}` : ""}</span> : <span className="text-slate-300">—</span>}
                  </td>
                  <td className="py-2.5 border-t border-slate-100 text-slate-500">{METHOD_LABEL[t.method] || "—"}</td>
                  <td className="py-2.5 border-t border-slate-100 text-slate-500">{t.owner || "—"}</td>
                  <td className="py-2.5 border-t border-slate-100 text-right mono font-medium">{fmt(t.amount)}</td>
                  <td className="py-2.5 border-t border-slate-100 pl-3"><StatusBadge status={t.status} /></td>
                </tr>
              ))}
              {items.length === 0 && <tr><td colSpan={9} className="py-8 text-center text-slate-400">Nothing matches.</td></tr>}
            </tbody>
          </table>
          {nextOffset != null && (
            <div className="text-center mt-4">
              <button disabled={loadingMore} onClick={loadMore} data-testid="transactions-load-more" className="h-9 px-4 rounded-lg border border-slate-200 bg-white text-[12.5px] font-semibold hover:bg-slate-50">
                {loadingMore ? "Loading…" : "Load more"}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
